import React, { useContext, useEffect, useState } from "react";
import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Input,
  MuiBox,
  MuiTypography,
  SubmitLoaderButton,
} from "components/atoms";
import { useTranslation } from "react-i18next";

//SVGs
import LockIcon from "assets/images/svg-icons/lockedCurrent.svg";
import GlobeIcon from "assets/images/svg-icons/globe.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  ActionAccessRequestAction,
  GetAccessRequestAction,
  GetSuggestionsMailAction,
} from "store/middlewares/general";
import { Check, Close } from "@mui/icons-material";
import SmartImage from "components/atoms/SmartImage";
import { CharacterName, firstCharacterName } from "utils/firstCharacterName";
import ShareAccessMenu from "./ShareAccessMenu";
import {
  CreateShareLinkFileFolderAction,
  DeletePermissionsFileFolderAction,
  GetSharedDetailsAction,
  GrantPermissionFileFolderAction,
  UpdatePermissionsFileFolderAction,
} from "store/middlewares/folders";
import { closeCenterModal } from "store/slices/CenterModal.slice";
import { openToast } from "store/slices/toast.slice";
import GeneralAccessMenu from "./GeneralAccessMenu";
import { useHandleCopy } from "utils/Commonfunctions";
import { VerifyLoginDetailsAction } from "store/middlewares/session/auth";
import ShareFolderModalSkeleton from "../SkeletonLoaders/ShareFolderModalSkeleton";
import { FirebaseContext } from "context/FirebaseContext"; // ← adjust path if required

const ShareFolderModal = ({ title, fileType, id, isEditor = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.profile);
  const {
    sharedDetail,
    sharedDetailLoading,
    accessRequest,
    // updateAccessRequestLoading,
  } = useSelector((state) => state.folders);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [declineLoading, setDeclineLoading] = useState(false);

  const [generalAccessType, setGeneralAccessType] = useState("restricted");
  const [publicAccessType, setPublicAccessType] = useState("view");
  const [accessType, setAccessType] = useState("view");
  const [userAccessType, setUserAccessType] = useState({});
  const [participantsValue, setParticipantsValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [grantPermissionLoading, setGrantPermissionLoading] = useState(false);
  const [handledRequests, setHandledRequests] = useState([]);
  const handleCopy = useHandleCopy();
  const [invalidEmails, setInvalidEmails] = useState([]);
  const [verifyingEmails, setVerifyingEmails] = useState([]);

  const { settings } = useContext(FirebaseContext);
  const shareUserLimit = settings?.driveShareUserLimit ?? null; // null = no limit enforced

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const isValidEmail = (value) => EMAIL_REGEX.test(value?.trim() ?? "");

  const normalizedEmail = (email) => email?.trim().toLowerCase();

  // Participants whose email failed verification (red border) are excluded
  const validParticipants = participants.filter(
    (person) => !invalidEmails.includes(normalizedEmail(person.email)),
  );

  // Count of users who already have access (exclude the owner)
  const currentSharedCount =
    sharedDetail?.shared_users?.filter(
      (item) => item?.id !== sharedDetail?.owner?.id,
    )?.length ?? 0;

  // Only valid (or still-verifying) participants count toward the limit
  const selectedCount = validParticipants.length;
  const isLimitReached =
    shareUserLimit !== null &&
    currentSharedCount + selectedCount >= shareUserLimit;

  const showLimitToast = () => {
    dispatch(
      openToast({
        type: "error",
        message: t(
          fileType === "folder"
            ? "modal.shareFileFolder.maxUsersLimitFolder"
            : "modal.shareFileFolder.maxUsersLimitFile",
          { count: shareUserLimit },
        ),
      }),
    );
  };

  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600-900
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900-1200

  // Responsive max length for middleEllipsis
  const getMaxLength = () => {
    if (isXs) return 15; // mobile
    if (isSm) return 35; // tablet
    if (isMd) return 35; // small desktop
    return 45; // large screens
  };

  useEffect(() => {
    dispatch(
      GetSharedDetailsAction({
        type: fileType,
        id,
      }),
    );
    dispatch(
      GetAccessRequestAction({
        ...(fileType === "file" ? { fileId: id } : { folderId: id }),
      }),
    );
  }, [dispatch]);

  //--------------user suggesion and select -------------
  const handleChange = (e) => {
    const value = e.target.value;
    setParticipantsValue(value);
    if (value) {
      dispatch(
        GetSuggestionsMailAction({
          suggestion: value.trim(),
          type: "drive",
          onSuccess: (data) => {
            setFilteredOptions(data);
          },
        }),
      );
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelect = (user) => {
    if (!user?.email) return;

    addParticipant(user);

    setParticipantsValue("");
    setFilteredOptions([]);
    setShowDropdown(false);
  };

  const handleParticipantKeyDown = (e) => {
    if (e.key !== "Enter") return;

    e.preventDefault();

    const email = participantsValue.trim();

    if (!email) return;

    // If it's not an email, don't add it
    if (!isValidEmail(email)) return;

    addParticipant({
      id: null,
      email,
      first_name: null,
      last_name: null,
      image_url: null,
    });

    setParticipantsValue("");
    setFilteredOptions([]);
    setShowDropdown(false);
  };

  //-------------- grant user permission for access -------------

  const handleGrantPermission = () => {
    if (
      grantPermissionLoading ||
      participants.length === 0 ||
      verifyingEmails.length > 0 ||
      validParticipants.length === 0 // <-- changed from !== participants.length
    ) {
      return;
    }

    setGrantPermissionLoading(true);

    const payload = {
      targetUserEmails: validParticipants.map((r) => r.email),
      permission: accessType,
      fileType: `${fileType}s`,
      fileId: id,
      onSuccess: () => {
        setGrantPermissionLoading(false);
        dispatch(closeCenterModal());
        dispatch(
          openToast({
            type: "info",
            message: t(
              validParticipants?.length === 1
                ? "modal.shareFileFolder.personAdded"
                : "modal.shareFileFolder.personsAdded",
              { value: validParticipants?.length },
            ),
          }),
        );
      },
      onError: () => {
        setGrantPermissionLoading(false);
      },
    };

    dispatch(GrantPermissionFileFolderAction(payload));
  };

  //-------------- change/update user permission or access ------------
  const handleAccessChange = (userId, newAccess) => {
    setUserAccessType((prev) => ({
      ...prev,
      [userId]: newAccess,
    }));

    const payload = {
      targetUserId: userId,
      permission: newAccess,
      fileType: `${fileType}s`,
      fileId: id,
    };
    dispatch(UpdatePermissionsFileFolderAction(payload));
  };

  useEffect(() => {
    if (sharedDetail?.general_access) {
      setPublicAccessType(sharedDetail?.general_access?.role ?? "view");
      setGeneralAccessType(
        sharedDetail?.general_access?.visibility ?? "restricted",
      );
    }
  }, [sharedDetail]);

  const addParticipant = (person) => {
    const email = person?.email?.trim();

    if (!email) return;

    const normalized = normalizedEmail(email);

    // Don't add yourself
    if (normalized === profile?.email?.toLowerCase()) {
      return;
    }

    // Already selected
    if (
      participants.some((item) => normalizedEmail(item.email) === normalized)
    ) {
      return;
    }

    // Respect share limit (existing access holders + valid selected participants)
    if (isLimitReached) {
      showLimitToast();
      return;
    }

    // Add immediately, even if verification fails
    const participant = {
      ...person,
      email,
    };

    setParticipants((prev) => [...prev, participant]);

    // Start verification
    setVerifyingEmails((prev) => [...prev, normalized]);

    dispatch(
      VerifyLoginDetailsAction({
        email,

        onSuccess: () => {
          setVerifyingEmails((prev) =>
            prev.filter((item) => item !== normalized),
          );

          setInvalidEmails((prev) =>
            prev.filter((item) => item !== normalized),
          );
        },

        onError: () => {
          setVerifyingEmails((prev) =>
            prev.filter((item) => item !== normalized),
          );

          setInvalidEmails((prev) =>
            prev.includes(normalized) ? prev : [...prev, normalized],
          );
        },
      }),
    );
  };

  const middleEllipsis = (str = "", maxLength = 40) => {
    if (!str || str.length <= maxLength) return str;

    const keep = maxLength - 1; // leave space for the "…"
    const start = Math.ceil(keep / 2);
    const end = Math.floor(keep / 2);

    return `${str.slice(0, start)}…${str.slice(-end)}`;
  };

  return (
    <MuiBox>
      <MuiBox
        sx={{
          p: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <MuiTypography fontWeight="600" variant="20" color="text.darkBlack">
          {title}
        </MuiTypography>
        {sharedDetailLoading ? (
          <ShareFolderModalSkeleton />
        ) : (
          <MuiBox
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <MuiBox
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              <MuiBox
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "12px",
                  position: "relative",
                }}
              >
                <MuiBox sx={{ display: "flex", gap: "8px", width: "100%" }}>
                  <Input
                    maxCharacter={80}
                    isBorderColor={true}
                    placeholder={t("modal.shareFileFolder.addPeopleGroups")}
                    value={participantsValue}
                    name={"participants"}
                    onKeyDown={handleParticipantKeyDown}
                    onChange={handleChange}
                    endAdornment={
                      <ShareAccessMenu
                        accessType={accessType}
                        setAccessType={setAccessType}
                        bgColor={"transparent"}
                      />
                    }
                  />
                </MuiBox>
                {showDropdown && filteredOptions?.length > 0 && (
                  <Paper
                    className="no-scrollbar"
                    sx={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      width: "100%",
                      zIndex: 10,
                      maxHeight: 200,
                      borderRadius: "8px",
                      mt: "4px",
                      border: "1px solid",
                      borderColor: "background.gray500",
                      boxShadow: " 0px 10px 18px -2px #0A090B12",
                      color: "text.darkBlack",
                      overflow: "auto",
                    }}
                  >
                    <List disablePadding>
                      {filteredOptions.map((option) => {
                        const selected = participants.some(
                          (v) => v.email === option.email,
                        );
                        return (
                          <ListItemButton
                            key={option.username}
                            disabled={
                              option.email === profile?.email || selected
                            }
                            onClick={() => handleSelect(option)}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <MuiBox
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <MuiBox
                                sx={{
                                  height: "28px",
                                  width: "28px",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                }}
                              >
                                <SmartImage
                                  src={option?.image_url}
                                  alt={"profile"}
                                  className="img-cover"
                                  fallback={
                                    <MuiBox
                                      sx={{
                                        bgcolor:
                                          option?.avatar?.backgroundColor,
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                      }}
                                    >
                                      <MuiTypography
                                        variant="12"
                                        color="text.white"
                                      >
                                        {option?.avatar?.initials}
                                      </MuiTypography>
                                    </MuiBox>
                                  }
                                />
                              </MuiBox>
                              <MuiTypography
                                fontWeight="500"
                                variant="14"
                                color="text.darkBlack"
                              >
                                {option?.email}
                              </MuiTypography>
                            </MuiBox>
                            {selected && (
                              <MuiBox sx={{ width: "fit-content" }}>
                                <Check />
                              </MuiBox>
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                )}
              </MuiBox>

              {participants?.length > 0 && (
                <MuiBox
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  {participants.map((item) => {
                    const email = normalizedEmail(item.email);
                    const isInvalid = invalidEmails.includes(email);
                    const isVerifying = verifyingEmails.includes(email);

                    return (
                      <Paper
                        key={item.id || item.email}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          p: "4px 8px",
                          border: "1px solid",
                          borderColor: isInvalid
                            ? "background.akiraRed"
                            : "background.gray500",
                          borderRadius: "6px",
                        }}
                      >
                        <MuiBox
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <MuiBox
                            sx={{
                              height: "28px",
                              width: "28px",
                              borderRadius: "50%",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            <SmartImage
                              src={item?.image_url}
                              alt="profile"
                              className="img-cover"
                              fallback={
                                <MuiBox
                                  sx={{
                                    bgcolor:
                                      item?.avatar?.backgroundColor ||
                                      "background.blue200",
                                    height: "100%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  }}
                                >
                                  <MuiTypography
                                    variant="12"
                                    color={
                                      item?.avatar?.backgroundColor
                                        ? "text.white"
                                        : "primary.main"
                                    }
                                  >
                                    {item?.avatar?.initials ||
                                      firstCharacterName(item.email)}
                                  </MuiTypography>
                                </MuiBox>
                              }
                            />
                          </MuiBox>

                          <MuiTypography
                            fontWeight="500"
                            variant="14"
                            color="text.darkBlack"
                            title={item.email} // full email on hover
                            sx={{
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              minWidth: 0,
                              maxWidth: {
                                xs: "180px", // prevent overflow on mobile
                                sm: "300px",
                                md: "500px",
                                lg: "600px",
                              },
                            }}
                          >
                            {middleEllipsis(item.email, getMaxLength())}
                          </MuiTypography>
                        </MuiBox>

                        {isVerifying && (
                          <CircularProgress
                            size={14}
                            sx={{ color: "primary.main" }}
                          />
                        )}

                        <IconButton
                          onClick={() => {
                            setParticipants((prev) =>
                              prev.filter(
                                (p) => normalizedEmail(p.email) !== email,
                              ),
                            );

                            setInvalidEmails((prev) =>
                              prev.filter((e) => e !== email),
                            );

                            setVerifyingEmails((prev) =>
                              prev.filter((e) => e !== email),
                            );
                          }}
                          sx={{
                            p: 0,
                            height: "16px",
                            width: "16px",
                          }}
                        >
                          <Close
                            sx={{
                              height: "100%",
                              width: "100%",
                            }}
                          />
                        </IconButton>
                      </Paper>
                    );
                  })}
                </MuiBox>
              )}
            </MuiBox>
            {participants?.length === 0 && (
              <MuiBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <MuiBox
                  sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
                >
                  <MuiTypography
                    fontWeight="600"
                    variant="14"
                    color="text.darkBlack"
                  >
                    {t("modal.shareFileFolder.generalAccess")}
                  </MuiTypography>
                  <MuiBox
                    sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    {generalAccessType === "restricted" ? (
                      <Avatar
                        sx={{
                          height: "40px",
                          width: "40px",
                          backgroundColor: "#C1F4D4",
                        }}
                      >
                        <MuiBox sx={{ height: "24px", width: "24px" }}>
                          <img src={LockIcon} className="img-contain" alt="" />
                        </MuiBox>
                      </Avatar>
                    ) : (
                      <Avatar
                        sx={{
                          height: "40px",
                          width: "40px",
                          backgroundColor: "#C1F4D4",
                        }}
                      >
                        <MuiBox sx={{ height: "24px", width: "24px" }}>
                          <img src={GlobeIcon} className="img-contain" alt="" />
                        </MuiBox>
                      </Avatar>
                    )}
                    <MuiBox
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: "8px", sm: "0px" },
                      }}
                    >
                      <MuiBox sx={{ display: "flex", flexDirection: "column" }}>
                        <GeneralAccessMenu
                          isDisable={!isEditor}
                          generalAccessType={generalAccessType}
                          setGeneralAccessType={(val) => {
                            const payload = { fileType };
                            if (val === "restricted") {
                              payload.visibility = "restricted";
                            } else {
                              payload.visibility = "public";
                              payload.role = "view";
                              setPublicAccessType("view");
                            }
                            if (fileType === "file") {
                              payload.fileId = id;
                            } else {
                              payload.folderId = id;
                            }
                            dispatch(CreateShareLinkFileFolderAction(payload));
                            setGeneralAccessType(val);
                          }}
                        />
                        <MuiTypography
                          fontWeight="400"
                          variant="14"
                          color="text.darkBlack"
                          sx={{ px: "8px" }}
                        >
                          {t(
                            generalAccessType === "restricted"
                              ? "modal.shareFileFolder.onlyPeopleWithTheLink"
                              : "modal.shareFileFolder.anyoneOnTheInternet",
                          )}
                        </MuiTypography>
                      </MuiBox>
                      {generalAccessType !== "restricted" && (
                        <ShareAccessMenu
                          isDisable={!isEditor}
                          accessType={publicAccessType}
                          setAccessType={(val) => {
                            const payload = { fileType, visibility: "public" };
                            if (val) {
                              payload.role = val;
                            }
                            if (fileType === "file") {
                              payload.fileId = id;
                            } else {
                              payload.folderId = id;
                            }
                            dispatch(CreateShareLinkFileFolderAction(payload));
                            setPublicAccessType(val);
                          }}
                        />
                      )}
                    </MuiBox>
                  </MuiBox>
                </MuiBox>

                {isEditor && (
                  <MuiBox
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    <MuiTypography
                      fontWeight="600"
                      variant="14"
                      color="text.darkBlack"
                    >
                      {t("modal.shareFileFolder.peopleWithAccess")}
                    </MuiTypography>
                    <MuiBox
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      <MuiBox
                        sx={{
                          height: "40px",
                          width: "40px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          flexShrink: 0,
                        }}
                      >
                        <SmartImage
                          src={
                            sharedDetail?.owner?.resized_image_urls?.small ??
                            sharedDetail?.owner?.mage_url
                          }
                          alt={"profile"}
                          className="img-cover"
                          fallback={
                            <MuiBox
                              sx={{
                                bgcolor:
                                  sharedDetail?.owner?.avatar?.backgroundColor,
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                              }}
                            >
                              <MuiTypography variant="14" color="text.white">
                                {sharedDetail?.owner?.avatar?.initials}
                              </MuiTypography>
                            </MuiBox>
                          }
                        />
                      </MuiBox>
                      <MuiBox
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          pl: "8px",
                          minWidth: 0,
                        }}
                      >
                        <MuiTypography
                          fontWeight="500"
                          variant="14"
                          color="text.darkBlack"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {`${sharedDetail?.owner?.first_name} ${sharedDetail?.owner?.last_name} (${t("modal.filter.owner")})`}
                        </MuiTypography>
                        <MuiTypography
                          fontWeight="400"
                          variant="14"
                          color="text.darkBlack"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {sharedDetail?.owner?.email}
                        </MuiTypography>
                      </MuiBox>
                    </MuiBox>
                    {sharedDetail?.shared_users
                      ?.filter((item) => item?.id !== sharedDetail?.owner?.id)
                      ?.map((item) => {
                        return (
                          <MuiBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                            key={item?.id}
                          >
                            <MuiBox
                              sx={{
                                height: "40px",
                                width: "40px",
                                borderRadius: "50%",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <SmartImage
                                src={
                                  item?.resized_image_urls?.small ??
                                  item?.image_url
                                }
                                alt="profile"
                                className="img-cover"
                                fallback={
                                  <MuiBox
                                    sx={{
                                      bgcolor: item?.avatar?.backgroundColor,
                                      height: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <MuiTypography
                                      variant="12"
                                      color="text.white"
                                    >
                                      {item?.avatar?.initials}
                                    </MuiTypography>
                                  </MuiBox>
                                }
                              />
                            </MuiBox>

                            <MuiBox
                              sx={{ display: "flex", pl: "8px", minWidth: 0 }}
                            >
                              <MuiBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  minWidth: 0,
                                }}
                              >
                                <MuiTypography
                                  fontWeight="500"
                                  variant="14"
                                  color="text.darkBlack"
                                >
                                  {`${item?.first_name || ""} ${item?.last_name || ""}${
                                    item?.id === profile?.id
                                      ? ` (${t("modal.shareFileFolder.you")})`
                                      : ""
                                  }`}
                                </MuiTypography>

                                <MuiTypography
                                  fontWeight="400"
                                  variant="14"
                                  color="text.darkBlack"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item?.email}
                                </MuiTypography>
                              </MuiBox>
                              <ShareAccessMenu
                                isRemoveAccess
                                isDisable={item?.role === "owner"}
                                accessType={
                                  item?.role === "owner"
                                    ? "edit"
                                    : userAccessType[item.id] ||
                                      item?.role ||
                                      "view"
                                }
                                setAccessType={(val) => {
                                  if (item?.role === "owner") return;
                                  if (val !== "remove") {
                                    handleAccessChange(item.id, val);
                                  } else {
                                    setUserAccessType((prev) => ({
                                      ...prev,
                                      [item.id]: val,
                                    }));
                                    const payload = {
                                      fileType: `${fileType}s`,
                                      fileId: id,
                                      targetUserId: item.id,
                                    };
                                    dispatch(
                                      DeletePermissionsFileFolderAction(
                                        payload,
                                      ),
                                    );
                                  }
                                }}
                              />
                            </MuiBox>
                          </MuiBox>
                        );
                      })}
                  </MuiBox>
                )}
                {isEditor &&
                  accessRequest?.filter(
                    (req) =>
                      !handledRequests.includes(req.id) &&
                      req.status === "pending",
                  )?.length > 0 && (
                    <MuiBox
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "12px",
                      }}
                    >
                      <MuiTypography
                        fontWeight="600"
                        variant="14"
                        color="text.darkBlack"
                      >
                        {t("modal.shareFileFolder.requestsReceived")}
                      </MuiTypography>
                      {accessRequest
                        .filter((req) => !handledRequests.includes(req.id))
                        .map((request) => (
                          <MuiBox
                            key={request.id}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "12px",
                              mb: "8px",
                            }}
                          >
                            <MuiBox
                              sx={{
                                height: "40px",
                                width: "40px",
                                borderRadius: "50%",
                                overflow: "hidden",
                                flexShrink: 0,
                              }}
                            >
                              <SmartImage
                                src={request?.requester?.image_url}
                                alt="profile"
                                className="img-cover"
                                fallback={
                                  <MuiBox
                                    sx={{
                                      bgcolor: "background.blue200",
                                      height: "100%",
                                      width: "100%",
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                    }}
                                  >
                                    <MuiTypography
                                      variant="12"
                                      color="primary.main"
                                    >
                                      {request?.requester?.first_name
                                        ? firstCharacterName(
                                            request.requester.first_name,
                                            request.requester.last_name,
                                          )
                                        : CharacterName(
                                            request?.requester?.email,
                                          )}
                                    </MuiTypography>
                                  </MuiBox>
                                }
                              />
                            </MuiBox>
                            <MuiBox
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                              }}
                            >
                              <MuiBox
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  mb: "8px",
                                }}
                              >
                                <MuiTypography
                                  fontWeight="500"
                                  variant="14"
                                  color="text.darkBlack"
                                >
                                  {request?.requester?.email}
                                </MuiTypography>
                                <MuiTypography
                                  fontWeight="400"
                                  variant="12"
                                  color="text.darkBlack"
                                >
                                  {t("modal.shareFileFolder.requested")}:{" "}
                                  {request?.permission === "edit"
                                    ? t("modal.shareFileFolder.edit")
                                    : t("modal.shareFileFolder.view")}
                                </MuiTypography>
                              </MuiBox>
                              {request?.message && (
                                <MuiBox
                                  sx={{
                                    bgcolor: "background.gray200",
                                    p: "12px",
                                    borderRadius: "8px",
                                    mb: "12px",
                                  }}
                                >
                                  <MuiTypography
                                    variant="14"
                                    color="text.darkBlack"
                                  >
                                    {request.message}
                                  </MuiTypography>
                                </MuiBox>
                              )}
                              <MuiBox sx={{ display: "flex", gap: "12px" }}>
                                <SubmitLoaderButton
                                  variant="contained"
                                  btnText={t("common.share")}
                                  sx={{
                                    borderRadius: "10px",
                                    px: 2,
                                    width: "fit-content",
                                  }}
                                  isDisable={
                                    acceptLoading ||
                                    declineLoading ||
                                    isLimitReached
                                  }
                                  isLoading={acceptLoading}
                                  onClick={() => {
                                    setAcceptLoading(true);
                                    const payload = {
                                      id: request?.id,
                                      action: "accept",
                                      onSuccess: () => {
                                        setAcceptLoading(false);
                                        setHandledRequests((prev) => [
                                          ...prev,
                                          request.id,
                                        ]);
                                        dispatch(
                                          openToast({
                                            type: "info",
                                            message: t(
                                              "accessGranted",
                                              "Access granted",
                                            ),
                                          }),
                                        );
                                        dispatch(
                                          GetSharedDetailsAction({
                                            type: fileType,
                                            id,
                                          }),
                                        );
                                      },
                                    };
                                    dispatch(
                                      ActionAccessRequestAction(payload),
                                    );
                                  }}
                                />
                                <SubmitLoaderButton
                                  variant="default"
                                  btnText={t("modal.shareFileFolder.decline")}
                                  sx={{
                                    px: 2,
                                    borderRadius: "10px",
                                    color: "primary.main",
                                  }}
                                  isDisable={declineLoading || acceptLoading}
                                  isLoading={declineLoading}
                                  onClick={() => {
                                    setDeclineLoading(true);
                                    const payload = {
                                      id: request?.id,
                                      action: "decline",
                                      onSuccess: () => {
                                        setDeclineLoading(false);
                                        setHandledRequests((prev) => [
                                          ...prev,
                                          request.id,
                                        ]);
                                        dispatch(
                                          GetSharedDetailsAction({
                                            type: fileType,
                                            id,
                                          }),
                                        );
                                      },
                                    };
                                    dispatch(
                                      ActionAccessRequestAction(payload),
                                    );
                                  }}
                                />
                              </MuiBox>
                            </MuiBox>
                          </MuiBox>
                        ))}
                    </MuiBox>
                  )}
              </MuiBox>
            )}
          </MuiBox>
        )}
      </MuiBox>
      {sharedDetail !== null && (
        <MuiBox
          sx={{
            display: "flex",
            gap: "12px",
            justifyContent: "end",
            backgroundColor: "background.gray200",
            p: "16px 24px",
          }}
        >
          {participants?.length > 0 ? (
            <>
              <Button
                variant="whiteContained"
                onClick={() => {
                  setParticipants([]);
                }}
              >
                {t("commonButton.cancel")}
              </Button>

              <SubmitLoaderButton
                variant="blueContained"
                btnText={t("common.share")}
                isDisable={
                  grantPermissionLoading ||
                  participants?.length === 0 ||
                  verifyingEmails.length > 0 ||
                  validParticipants.length === 0
                }
                isLoading={grantPermissionLoading}
                onClick={handleGrantPermission}
              />
            </>
          ) : (
            <Button
              variant="whiteContained"
              onClick={() => {
                if (
                  sharedDetail?.general_access !== null &&
                  !!sharedDetail?.general_access?.shared_link
                ) {
                  handleCopy(
                    sharedDetail?.general_access?.shared_link,
                    t("common.copiedLinktoClipboard"),
                  );
                } else {
                  const payload = {
                    fileType,
                    visibility: "restricted",
                    onSuccess: (data) => {
                      handleCopy(
                        data?.access_link,
                        t("common.copiedLinktoClipboard"),
                      );
                    },
                  };
                  if (fileType === "file") {
                    payload.fileId = id;
                  } else {
                    payload.folderId = id;
                  }
                  dispatch(CreateShareLinkFileFolderAction(payload));
                }
              }}
            >
              {t("common.copyLink")}
            </Button>
          )}
        </MuiBox>
      )}
    </MuiBox>
  );
};

export default ShareFolderModal;
